import { z } from "zod";
import { scriptSentences } from "./language-source.js";
import type { Alignment } from "./subtitles.js";

/**
 * When a launch film's voice says each line, measured from the recording.
 *
 * The picture used to be timed by fixed fractions of the film while the voice
 * read at its own pace, and nothing compared the two — so a hook that took
 * nine seconds to say was still on screen for six. With this the picture is
 * told when each line is actually heard.
 */

/** Hook, problem, turn, product, proof, close — the film's sections, in order. */
export const LAUNCH_SECTION_COUNT = 6;

/** The quiet after the last word before the film may end, in seconds. */
const CLOSE_TAIL_SECONDS = 2;
/** How far past the look's length a long voice may push the film. */
const MAX_STRETCH = 1.2;
/** The shortest a section may be on screen, in seconds. */
const MIN_SECTION_SECONDS = 1;

export interface TimedSentence {
  text: string;
  /** Seconds from the first frame. */
  start: number;
  end: number;
  /** Which paragraph of the script it belongs to. */
  section: number;
}

export interface VoiceTimeline {
  sentences: TimedSentence[];
  /** One per section, or null when the script is not six paragraphs. */
  sections: { start: number; end: number }[] | null;
  /** When the last word ends. */
  voiceEnd: number;
}

export const voiceTimelineSchema = z.object({
  sentences: z.array(
    z.object({ text: z.string(), start: z.number(), end: z.number(), section: z.number().int() }),
  ),
  sections: z.array(z.object({ start: z.number(), end: z.number() })).nullable(),
  voiceEnd: z.number(),
});

/** The script's paragraphs, which are its sections; blank and whitespace-only lines separate them. */
export function launchScriptSections(script: string): string[] {
  return script
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
}

const SPOKEN = /[\p{L}\p{N}]/u;
const spokenCharacters = (text: string): string[] =>
  [...text.normalize("NFC").toLocaleLowerCase()].filter((char) => SPOKEN.test(char));

/**
 * The alignment's spoken characters, each with its time.
 *
 * Anything inside `<…>` is markup the provider echoes back (a pause is sent as
 * `<break time="1s" />`), and its letters were never spoken.
 */
function spokenStream(alignment: Alignment): { char: string; start: number; end: number }[] {
  const stream: { char: string; start: number; end: number }[] = [];
  let inTag = false;
  alignment.characters.forEach((raw, index) => {
    if (raw === "<") inTag = true;
    if (!inTag) {
      for (const char of spokenCharacters(raw)) {
        stream.push({ char, start: alignment.starts[index]!, end: alignment.ends[index]! });
      }
    }
    if (raw === ">") inTag = false;
  });
  return stream;
}

/**
 * Maps the alignment onto the script's sentences, in order, by spoken
 * characters alone — so spacing, punctuation, case and pause markup on either
 * side cannot throw it. Null when there is no alignment or it says something
 * other than the script.
 */
export function voiceTimeline(script: string, alignment: Alignment | null): VoiceTimeline | null {
  if (!alignment || alignment.characters.length === 0) return null;
  const stream = spokenStream(alignment);
  const paragraphs = launchScriptSections(script);

  const sentences: TimedSentence[] = [];
  /** Sentences with no letters, timed once the next spoken one is. */
  const waiting: TimedSentence[] = [];
  let cursor = 0;
  for (const [section, paragraph] of paragraphs.entries()) {
    for (const text of scriptSentences(paragraph)) {
      const chars = spokenCharacters(text);
      const sentence: TimedSentence = { text, start: 0, end: 0, section };
      sentences.push(sentence);
      if (chars.length === 0) {
        waiting.push(sentence);
        continue;
      }
      for (const [offset, char] of chars.entries()) {
        if (stream[cursor + offset]?.char !== char) return null;
      }
      sentence.start = stream[cursor]!.start;
      sentence.end = stream[cursor + chars.length - 1]!.end;
      cursor += chars.length;
      for (const silent of waiting.splice(0)) {
        silent.start = sentence.start;
        silent.end = sentence.start;
      }
    }
  }
  if (cursor !== stream.length || cursor === 0) return null;
  const voiceEnd = stream[stream.length - 1]!.end;
  for (const silent of waiting) {
    silent.start = voiceEnd;
    silent.end = voiceEnd;
  }

  const sections =
    paragraphs.length === LAUNCH_SECTION_COUNT
      ? paragraphs.map((_, index) => {
          const own = sentences.filter((sentence) => sentence.section === index);
          return { start: own[0]?.start ?? voiceEnd, end: own.at(-1)?.end ?? voiceEnd };
        })
      : null;

  return { sentences, sections, voiceEnd };
}

const tenth = (seconds: number) => Math.round(seconds * 10) / 10;

/** The look's length, or longer when the voice needs it — by at most a fifth. */
export function filmSecondsFor(lookSeconds: number, timeline: VoiceTimeline | null): number {
  if (!timeline) return lookSeconds;
  const needed = timeline.voiceEnd + CLOSE_TAIL_SECONDS;
  if (needed <= lookSeconds) return lookSeconds;
  // Less a hair, so a length that is already a whole tenth is not pushed up by float noise.
  return Math.ceil(Math.min(needed, lookSeconds * MAX_STRETCH) * 10 - 1e-9) / 10;
}

/**
 * Where each section starts on the film, from its voice: seven boundaries,
 * the first 0 and the last the film's end. Each section keeps at least a
 * second, the close included, even when the voice runs to the end.
 */
export function sectionBeats(timeline: VoiceTimeline, filmSeconds: number): number[] | null {
  if (!timeline.sections) return null;
  const beats = [0, ...timeline.sections.slice(1).map((section) => tenth(section.start)), filmSeconds];
  for (let index = beats.length - 2; index >= 1; index -= 1) {
    beats[index] = Math.min(beats[index]!, tenth(beats[index + 1]! - MIN_SECTION_SECONDS));
  }
  for (let index = 1; index < beats.length - 1; index += 1) {
    beats[index] = Math.max(beats[index]!, tenth(beats[index - 1]! + MIN_SECTION_SECONDS));
  }
  return beats;
}

/** Seconds as the prompt writes them: a tenth at most, no trailing zeros. */
export function formatSeconds(seconds: number): string {
  return String(tenth(seconds));
}

/** The voice timeline a launch job kept in its result, or null for one made before timings were kept. */
export function launchVersionTimeline(result: unknown): VoiceTimeline | null {
  const raw = (result as { narration?: { timeline?: unknown } } | null)?.narration?.timeline;
  const parsed = voiceTimelineSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}
